<template>
  <div class="ml-branch">
    <!-- 容器子级 → 手风琴 pills（点击展开/折叠其内容） -->
    <div class="ml-pills" v-if="branches.length">
      <button
        v-for="b in branches"
        :key="b.code"
        type="button"
        class="ml-pill"
        :class="{ on: active === b.code, picked: count(b) > 0 }"
        @click="toggle(b.code)"
      >
        <span class="t">{{ short(b) }}</span>
        <span class="n" v-if="count(b) > 0">{{ count(b) }}</span>
        <i class="caret" :class="{ up: active === b.code }"></i>
      </button>
    </div>

    <!-- 展开的容器：递归渲染其内容 -->
    <transition name="ml-fold">
      <div class="ml-fold" v-if="activeNode">
        <ml-branch :node="activeNode" :sel="sel" @toggle-leaf="bubble" />
      </div>
    </transition>

    <!-- 叶子子级 → 可选 chips -->
    <div class="ml-chips" v-if="leaves.length">
      <button
        v-for="l in leaves"
        :key="l.code"
        type="button"
        class="ml-chip"
        :class="{ sel: !!sel[l.code] }"
        @click="$emit('toggle-leaf', l)"
      >{{ short(l) }}</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MlBranch',
  props: {
    node: { type: Object, required: true },
    sel: { type: Object, default: () => ({}) }
  },
  data() {
    return { active: null }
  },
  computed: {
    kids() {
      return (this.node && this.node.children) || []
    },
    branches() {
      return this.kids.filter(c => c.children && c.children.length)
    },
    leaves() {
      return this.kids.filter(c => !c.children || !c.children.length)
    },
    activeNode() {
      return this.branches.find(b => b.code === this.active) || null
    }
  },
  methods: {
    toggle(code) {
      this.active = this.active === code ? null : code
    },
    bubble(leaf) {
      this.$emit('toggle-leaf', leaf)
    },
    // 去掉父级名前缀：大国标马超 → 马超；大国标(父=国标)不受影响
    short(n) {
      const p = this.node && this.node.name
      if (p && n.name && n.name.indexOf(p) === 0 && n.name.length > p.length) {
        return n.name.slice(p.length)
      }
      return n.name
    },
    // 该节点子树内已选叶子数
    count(n) {
      let c = 0
      const walk = nodes => (nodes || []).forEach(x => {
        if (x.children && x.children.length) walk(x.children)
        else if (this.sel[x.code]) c++
      })
      walk(n.children)
      return c
    }
  }
}
</script>

<style scoped lang="less">
/* 遵循 DESIGN-new.md：紧圆角(≤4px,控件3px)、单一主色按意图取用、4px 间距阶、typography 令牌 */
@c-primary: var(--primary, #FF5A1F);
@c-primary-soft: var(--primary-soft, #FFEDE6);
@c-border: var(--border-soft, #E8E4DE);
@c-tag: var(--bg-tag, #F0EDE8);
@c-main: var(--text-main, #1A1D24);
@c-sec: var(--text-secondary, #5A5F6B);

.ml-branch { width: 100%; }

.ml-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

/* 手风琴 Tab：控件圆角 3px(rounded.md)，激活=主色实心(激活态用主色，符合规范) */
.ml-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid @c-border;
  background: #fff;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 600;
  color: @c-sec;
  cursor: pointer;
  transition: all .2s cubic-bezier(.22, .9, .24, 1);
  outline: none;

  &:hover { border-color: @c-primary; color: @c-primary; }
  &.picked { border-color: @c-primary; color: @c-primary; }
  &.on {
    background: @c-primary;
    border-color: @c-primary;
    color: #fff;
  }
  .n {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 15px;
    height: 15px;
    padding: 0 4px;
    border-radius: 2px;
    background: @c-primary-soft;
    color: @c-primary;
    font-size: 11px;
    font-weight: 700;
    line-height: 15px;
  }
  &.on .n { background: rgba(255, 255, 255, .26); color: #fff; }
  .caret {
    width: 0; height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid currentColor;
    transition: transform .2s cubic-bezier(.22, .9, .24, 1);
    opacity: .65;
  }
  .caret.up { transform: rotate(180deg); }
}

/* 折叠面板：容器圆角 4px(rounded.lg)，浅暖灰分隔底 */
.ml-fold {
  margin: 0 0 8px;
  padding: 10px 12px;
  background: @c-tag;
  border-radius: 4px;
}

.ml-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 248px;
  overflow-y: auto;
}

/* 叶子 chip：与 flat 单选 chip 统一——白底1px细边3px紧角30px；选中=主色实心+白字 */
.ml-chip {
  height: 30px;
  padding: 0 12px;
  border: 1px solid @c-border;
  background: #fff;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 400;
  color: @c-main;
  cursor: pointer;
  transition: all .2s cubic-bezier(.22, .9, .24, 1);
  outline: none;

  &:hover { border-color: @c-primary; color: @c-primary; }
  &.sel {
    background: @c-primary;
    border-color: @c-primary;
    color: #fff;
    font-weight: 600;
  }
}

.ml-fold-enter-active, .ml-fold-leave-active { transition: opacity .2s cubic-bezier(.22, .9, .24, 1); }
.ml-fold-enter, .ml-fold-leave-to { opacity: 0; }
</style>
